import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CakeIcon from '@mui/icons-material/Cake';
import FlagIcon from '@mui/icons-material/Flag';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaletteIcon from '@mui/icons-material/Palette';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Horse } from '@phosphor-icons/react';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx, smartPageSx } from '../../theme/smartStableTheme';
import { todayDateInput } from '../../utils/dateUtils';

const COPY = {
  ar: {
    title: 'إحصائياتي',
    subtitle: 'قراءة منظمة لبيانات الخيل: اللون، السلالة، المنشأ، العمر، الجنس، والسكن.',
    totalHorses: 'إجمالي الخيل',
    totalCosts: 'إجمالي التكاليف',
    averageAge: 'متوسط العمر',
    accommodated: 'الخيل المسكنة',
    withRfid: 'لها شريحة',
    colors: 'توزيع الألوان',
    breeds: 'توزيع السلالات',
    origins: 'بلد المنشأ',
    ageGroups: 'الفئات العمرية',
    gender: 'الجنس',
    accommodation: 'حالة السكن',
    serviceMix: 'لمحة الخدمات',
    young: 'أقل من 3 سنوات',
    adult: '3 إلى 9 سنوات',
    senior: '10 سنوات فأكثر',
    unknown: 'غير محدد',
    mares: 'إناث',
    stallions: 'ذكور',
    accommodatedLabel: 'مسكنة',
    notAccommodated: 'غير مسكنة',
    years: 'سنة',
    noData: 'لا توجد بيانات كافية للعرض',
    noLink: 'هذا الحساب غير مربوط بمالك.',
  },
  en: {
    title: 'My Statistics',
    subtitle: 'A horse-first view by color, breed, origin, age, gender, and accommodation.',
    totalHorses: 'Total horses',
    totalCosts: 'Total costs',
    averageAge: 'Average age',
    accommodated: 'Accommodated horses',
    withRfid: 'With RFID',
    colors: 'Color distribution',
    breeds: 'Breed distribution',
    origins: 'Country of origin',
    ageGroups: 'Age groups',
    gender: 'Gender',
    accommodation: 'Accommodation status',
    serviceMix: 'Service snapshot',
    young: 'Under 3 years',
    adult: '3 to 9 years',
    senior: '10+ years',
    unknown: 'Unknown',
    mares: 'Mares',
    stallions: 'Stallions',
    accommodatedLabel: 'Accommodated',
    notAccommodated: 'Not accommodated',
    years: 'years',
    noData: 'Not enough data to show',
    noLink: 'This account is not linked to an owner profile.',
  },
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatMoney = (value) => `${formatNumber(value)} SAR`;

const calcAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const date = new Date(dateOfBirth);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1;
  return Math.max(age, 0);
};

const pickLabel = (horse, enKey, arKey, lang, fallback) =>
  (lang === 'ar' ? horse[arKey] || horse[enKey] : horse[enKey] || horse[arKey]) || fallback;

const serviceLabel = (service, lang) => {
  const labels = {
    ar: {
      vetclinic: 'العيادة',
      farrier: 'البيطار',
      transportation: 'النقل',
      accommodation: 'السكن',
    },
    en: {
      vetclinic: 'Vet clinic',
      farrier: 'Farrier',
      transportation: 'Transport',
      accommodation: 'Accommodation',
    },
  };
  return labels[lang]?.[service] || service || '-';
};

const Metric = ({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) => (
  <Paper elevation={0} sx={{ ...smartCardSx, p: 2.2 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
      <Box>
        <Typography variant="body2" sx={{ color, fontWeight: 900 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ProgressRow = ({ label, value, amount, max }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{label}</Typography>
      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{amount}</Typography>
    </Stack>
    <Box sx={{ height: 8, mt: 0.8, borderRadius: 99, bgcolor: COLORS.cardSoft }}>
      <Box sx={{ height: '100%', width: `${Math.max(5, (Number(value || 0) / Math.max(max, 1)) * 100)}%`, borderRadius: 99, bgcolor: COLORS.gold }} />
    </Box>
  </Box>
);

const DistributionCard = ({ title, icon, rows, emptyLabel }) => {
  const max = Math.max(...rows.map((row) => row.value), 1);
  return (
    <Paper elevation={0} sx={{ ...smartCardSx, p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold, width: 34, height: 34 }}>{icon}</Avatar>
        <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {title}
        </Typography>
      </Stack>
      <Stack spacing={1.45}>
        {rows.length ? (
          rows.map((row) => (
            <ProgressRow key={row.label} label={row.label} value={row.value} amount={formatNumber(row.value)} max={max} />
          ))
        ) : (
          <Typography sx={{ color: COLORS.muted }}>{emptyLabel}</Typography>
        )}
      </Stack>
    </Paper>
  );
};

const ClientHorseStats = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const ownerId = localStorage.getItem('owner_id');
  const [horses, setHorses] = useState([]);
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!ownerId || ownerId === 'null' || ownerId === 'undefined') {
        setError(text.noLink);
        setLoading(false);
        return;
      }

      try {
        const to = todayDateInput();
        const [horsesRes, statementRes] = await Promise.all([
          api.get(`/owners/${ownerId}/horses-with-details`),
          api.get(`/accounting-reports/owner-statements?owner_id=${ownerId}&from=2020-01-01&to=${to}`),
        ]);
        if (!alive) return;
        setHorses(Array.isArray(horsesRes.data) ? horsesRes.data : []);
        setStatement(Array.isArray(statementRes.data) ? statementRes.data : []);
      } catch {
        if (alive) setError(text.noData);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [ownerId, text.noData, text.noLink]);

  const summary = useMemo(() => {
    const totalCosts = statement.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const countBy = (getKey) => {
      const map = horses.reduce((acc, horse) => {
        const key = getKey(horse) || text.unknown;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(map)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
    };

    const ages = horses.map((horse) => calcAge(horse.date_of_birth)).filter((age) => age !== null);
    const averageAge = ages.length ? ages.reduce((sum, age) => sum + age, 0) / ages.length : 0;
    const ageRows = [
      { label: text.young, value: ages.filter((age) => age < 3).length },
      { label: text.adult, value: ages.filter((age) => age >= 3 && age < 10).length },
      { label: text.senior, value: ages.filter((age) => age >= 10).length },
      { label: text.unknown, value: horses.length - ages.length },
    ].filter((row) => row.value > 0);

    const byService = statement.reduce((map, row) => {
      const key = row.service_type || 'service';
      map[key] = (map[key] || 0) + Number(row.amount || 0);
      return map;
    }, {});
    const serviceRows = Object.entries(byService).sort((a, b) => b[1] - a[1]);
    return {
      totalCosts,
      averageAge,
      accommodated: horses.filter((horse) => horse.room_name || horse.stable_name).length,
      withRfid: horses.filter((horse) => horse.rfid_chip_number).length,
      colorRows: countBy((horse) => pickLabel(horse, 'color_english', 'color_arabic', lang, text.unknown)),
      breedRows: countBy((horse) => pickLabel(horse, 'breed_english', 'breed_arabic', lang, text.unknown)),
      originRows: countBy((horse) => pickLabel(horse, 'country_english', 'country_arabic', lang, text.unknown)),
      genderRows: countBy((horse) => (horse.gender === 'M' ? text.mares : horse.gender === 'S' ? text.stallions : text.unknown)),
      accommodationRows: [
        { label: text.accommodatedLabel, value: horses.filter((horse) => horse.room_name || horse.stable_name).length },
        { label: text.notAccommodated, value: horses.filter((horse) => !horse.room_name && !horse.stable_name).length },
      ].filter((row) => row.value > 0),
      ageRows,
      serviceRows,
      maxService: Math.max(...serviceRows.map(([, value]) => value), 1),
    };
  }, [horses, lang, statement, text]);

  if (loading) {
    return (
      <Box sx={{ ...smartPageSx, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ ...smartPageSx, px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {text.title}
          </Typography>
          <Typography sx={{ color: COLORS.muted, mt: 0.6 }}>{text.subtitle}</Typography>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 2.5 }}>
          <Metric icon={<Horse size={24} weight="fill" />} label={text.totalHorses} value={horses.length} />
          <Metric icon={<CakeIcon />} label={text.averageAge} value={`${summary.averageAge.toFixed(1)} ${text.years}`} color={COLORS.blue} soft={COLORS.blueSoft} />
          <Metric icon={<ReceiptLongIcon />} label={text.withRfid} value={summary.withRfid} color={COLORS.green} soft={COLORS.greenSoft} />
          <Metric icon={<HomeWorkIcon />} label={text.accommodated} value={summary.accommodated} color={COLORS.warning} soft={COLORS.warningSoft} />
          <Metric icon={<AccountBalanceWalletIcon />} label={text.totalCosts} value={formatMoney(summary.totalCosts)} color={COLORS.rose} soft={COLORS.roseSoft} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
          <DistributionCard title={text.colors} icon={<PaletteIcon />} rows={summary.colorRows} emptyLabel={text.noData} />
          <DistributionCard title={text.breeds} icon={<Horse size={22} weight="fill" />} rows={summary.breedRows} emptyLabel={text.noData} />
          <DistributionCard title={text.origins} icon={<FlagIcon />} rows={summary.originRows} emptyLabel={text.noData} />
          <DistributionCard title={text.ageGroups} icon={<CakeIcon />} rows={summary.ageRows} emptyLabel={text.noData} />
          <DistributionCard title={text.gender} icon={<Horse size={22} weight="fill" />} rows={summary.genderRows} emptyLabel={text.noData} />
          <DistributionCard title={text.accommodation} icon={<HomeWorkIcon />} rows={summary.accommodationRows} emptyLabel={text.noData} />

          <Paper elevation={0} sx={{ ...smartCardSx, p: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ color: COLORS.gold }} />
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {text.serviceMix}
              </Typography>
            </Stack>
            <Stack spacing={1.6}>
              {summary.serviceRows.length ? (
                summary.serviceRows.map(([service, value]) => (
                  <ProgressRow key={service} label={serviceLabel(service, lang)} value={value} amount={formatMoney(value)} max={summary.maxService} />
                ))
              ) : (
                <Typography sx={{ color: COLORS.muted }}>{text.noData}</Typography>
              )}
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientHorseStats;
